import { ProviderProfile } from '@/services/Providers';
import { CoverageType, PolicyType, Quote, QuoteRate, QuoteTerm } from '@/services/Quotes';
import { Guid } from '@constellation-mutual/guid';
import moment from 'moment';
import Vue from 'vue';

// Constants.

const defaultDateFormat: string = 'MM/DD/YYYY';
const defaultCurrencyConfig = {
     fractionCount: 0,
     symbol: '$',
     symbolSpacing: false,
};
type CurrencyConfig = typeof defaultCurrencyConfig;
// const dash: string = '-';
const enDash: string = '–';

// Helper functions.

const formatDate = (
     dateAsStringOrMoment: moment.Moment | string,
     dateFormat: string = defaultDateFormat,
): string => {
     const dateAsMoment: moment.Moment =
          typeof dateAsStringOrMoment === 'string'
               ? moment(dateAsStringOrMoment)
               : dateAsStringOrMoment;
     return dateAsMoment.format(dateFormat);
};

const formatDollars = (
     dollars: number,
     currencyConfig: CurrencyConfig = defaultCurrencyConfig,
): string => Vue.filter('currency')(dollars, currencyConfig);

// Argument validators.

export const throwIfInvalidRateTermIndex = (quote: any, termIndex: any): void => {
     const isValidTermIndex: boolean =
          typeof termIndex === 'number' &&
          !Number.isNaN(termIndex) &&
          termIndex >= 0 &&
          termIndex < Quote.getRate(quote).terms.length;
     if (!isValidTermIndex) {
          throw new TypeError(`Invalid \`termIndex\`: '${termIndex}'`);
     }
};

export const throwIfInvalidProviderProfileIndex = (quote: any, profileIndex: any): void => {
     const isValidProfileIndex: boolean =
          typeof profileIndex === 'number' &&
          !Number.isNaN(profileIndex) &&
          profileIndex >= 0 &&
          profileIndex < quote.coverageScope.providerProfiles.length;
     if (!isValidProfileIndex) {
          throw new TypeError(`Invalid \`profileIndex\`: '${profileIndex}'`);
     }
};

export const throwIfInvalidQuote = (quote: any): void => {
     if (!(quote instanceof Quote)) {
          throw new TypeError(`Invalid \`quote\`: '${JSON.stringify(quote, undefined, 2)}'`);
     }
};

// Quote filters.

type QuoteFilter = (quote: Quote, ...args: any[]) => string;

namespace QuoteFilter {
     /**
      * When a filter throws an exception, Vue silently fails by simply not rendering the component
      * that invoked it. This function wraps a filter and, in the event of an exception, does the
      * following for improved debugging: 1.) the exception is logged, 2.) the arguments sent to
      * the original filter are logged, and 3.) the exception's message is returned, which will show
      * up in the UI.
      * @param filter
      */
     export const handleExceptions = (filter: QuoteFilter): QuoteFilter => {
          return (quote: Quote, ...args: any[]) => {
               try {
                    return filter(quote, ...args);
               } catch (error) {
                    console.error(error);
                    console.log(args);
                    return error.message;
               }
          };
     };
}

export const quoteExpiry = (quote: Quote, dateFormat: string = defaultDateFormat): string => {
     throwIfInvalidQuote(quote);

     const expiryMoment: moment.Moment = moment(quote.timestampCreated)
          .endOf('day')
          .add(30, 'days');
     return formatDate(expiryMoment, dateFormat);
};

export const isQuoteExpired = (quote): string => {
     const now = formatDate(moment(new Date()), defaultDateFormat);
     const expiryDate = quoteExpiry(quote);
     if (now > expiryDate) {
          return 'expired';
     }
};

export const quoteId = (quote: Quote): string => {
     throwIfInvalidQuote(quote);

     return Guid.formatShort(quote.id);
};

// Coverage scope filters.

export const quoteClinicPriorActsDate = (
     quote: Quote,
     dateFormat: string = defaultDateFormat,
): string => {
     throwIfInvalidQuote(quote);

     const clinicPriorActsDate: moment.Moment = moment(
          quote.coverageScope.clinicPriorActsDate,
     ).startOf('day');
     return formatDate(clinicPriorActsDate, dateFormat);
};

export const quoteCoverageType = (quote: Quote): string => {
     throwIfInvalidQuote(quote);

     switch (quote.coverageScope.coverageType) {
          case CoverageType.clinic:
               return 'Clinic';

          case CoverageType.individual:
               return 'Individual';

          default:
               throw new TypeError(
                    `Invalid \`quote.coverageScope.coverageType\`: '${
                         quote.coverageScope.coverageType
                    }'`,
               );
     }
};

export const quoteProviderNickname = (quote: Quote, profileIndex: number): string => {
     throwIfInvalidQuote(quote);
     throwIfInvalidProviderProfileIndex(quote, profileIndex);

     const profile: ProviderProfile = quote.coverageScope.providerProfiles[profileIndex];
     return profile.nickname || `Dentist ${profileIndex + 1}`;
};

export const quoteProviderPriorActsDate = (
     quote: Quote,
     profileIndex: number,
     dateFormat: string = defaultDateFormat,
): string => {
     throwIfInvalidQuote(quote);
     throwIfInvalidProviderProfileIndex(quote, profileIndex);

     const profile: ProviderProfile = quote.coverageScope.providerProfiles[profileIndex];
     return formatDate(profile.priorActsDate, dateFormat);
};

export const quoteProviderProcedures = (
     quote: Quote,
     profileIndex: number,
): Array<{ name: string; value: string }> => {
     throwIfInvalidQuote(quote);
     throwIfInvalidProviderProfileIndex(quote, profileIndex);

     const calcValue = (bool: boolean): 'Yes' | 'No' => (bool ? 'Yes' : 'No');
     const profile: ProviderProfile = quote.coverageScope.providerProfiles[profileIndex];
     return [
          { name: 'Injects Botox®', value: calcValue(profile.procedures.injectsBotox) },
          {
               name: 'Injects Dermal Filler',
               value: calcValue(profile.procedures.injectsDermalFiller),
          },
          { name: 'Places Surgical Implants', value: calcValue(profile.procedures.placesImplants) },
          {
               name: 'Extracts Impacted Third Molars',
               value: calcValue(profile.procedures.extractsImpactedThirdMolars),
          },
     ];
};

// Policy params filters.

export const quotePolicyStartDate = (
     quote: Quote,
     dateFormat: string = defaultDateFormat,
): string => {
     throwIfInvalidQuote(quote);

     return formatDate(quote.policyParams.startDate, dateFormat);
};

export const quotePolicyType = (quote: Quote): string => {
     throwIfInvalidQuote(quote);

     switch (quote.policyParams.policyType) {
          case PolicyType.claimsMade:
               return 'Claims-Made and Reported';

          case PolicyType.occurrence:
               return 'Occurrence';

          default:
               throw new TypeError(
                    `Invalid \`quote.policyParams.policyType\`: '${quote.policyParams.policyType}'`,
               );
     }
};

// Practice filters.

export const quotePracticeSpecialty = (quote: Quote): string => {
     throwIfInvalidQuote(quote);

     // TODO: Lookup quote.practice.specialtyId.
     return 'Dentistry';
};

export const quotePracticeState = (quote: Quote): string => {
     throwIfInvalidQuote(quote);

     return quote.practice.region.state;
};

// Rate filters.

export const quoteRateLiabilityLimitsIncidentDollars = (
     quote: Quote,
     currencyConfig: CurrencyConfig = defaultCurrencyConfig,
): string => {
     throwIfInvalidQuote(quote);

     const rate: QuoteRate = Quote.getRate(quote);
     return formatDollars(rate.liabilityLimits.incidentDollars, currencyConfig);
};

export const quoteRateLiabilityLimitsAggregateDollars = (
     quote: Quote,
     currencyConfig: CurrencyConfig = defaultCurrencyConfig,
): string => {
     throwIfInvalidQuote(quote);

     const rate: QuoteRate = Quote.getRate(quote);
     return formatDollars(rate.liabilityLimits.aggregateDollars, currencyConfig);
};

export const quoteRateLiabilityLimits = (
     quote: Quote,
     longOrShort: 'long' | 'short' = 'long',
     currencyConfig: CurrencyConfig = defaultCurrencyConfig,
): string => {
     throwIfInvalidQuote(quote);

     const rate: QuoteRate = Quote.getRate(quote);

     let incidentValue: number = rate.liabilityLimits.incidentDollars;
     let aggregateValue: number = rate.liabilityLimits.aggregateDollars;
     if (longOrShort === 'short') {
          incidentValue /= 1e6;
          aggregateValue /= 1e6;
     }

     let incidentText: string = formatDollars(incidentValue, currencyConfig);
     let aggregateText: string = formatDollars(aggregateValue, currencyConfig);
     if (longOrShort === 'short') {
          incidentText += 'M';
          aggregateText += 'M';
     }

     return `${incidentText} / ${aggregateText}`;
};

export const quoteRateTermDateRange = (
     quote: Quote,
     termIndex: number,
     dateFormat: string = defaultDateFormat,
): string => {
     throwIfInvalidQuote(quote);
     throwIfInvalidRateTermIndex(quote, termIndex);

     const rate: QuoteRate = Quote.getRate(quote);
     const term: QuoteTerm = rate.terms[termIndex];
     return `${formatDate(term.startDate, dateFormat)}${enDash}${formatDate(
          term.stopDateExclusive,
          dateFormat,
     )}`;
};

export const quoteRateTermPremiumDollars = (
     quote: Quote,
     termIndex: number,
     currencyConfig: CurrencyConfig = defaultCurrencyConfig,
): string => {
     throwIfInvalidQuote(quote);
     throwIfInvalidRateTermIndex(quote, termIndex);

     const rate: QuoteRate = Quote.getRate(quote);
     const term: QuoteTerm = rate.terms[termIndex];
     return formatDollars(term.premiumDollars, currencyConfig);
};

export const quoteRateTermYearOrdinal = (quote: Quote, termIndex: number): string => {
     throwIfInvalidQuote(quote);
     throwIfInvalidRateTermIndex(quote, termIndex);

     let ordinal: string = 'th';
     const year: number = termIndex + 1;
     const yearTens: number = year % 100;
     if (yearTens !== 11 && yearTens !== 12 && yearTens !== 13) {
          const yearOnes: number = year % 10;
          if (yearOnes === 1) {
               ordinal = 'st';
          } else if (yearOnes === 2) {
               ordinal = 'nd';
          } else if (yearOnes === 3) {
               ordinal = 'rd';
          }
     }

     return `${year}${ordinal} Term`;
};

export default () => {
     interface QuoteFilterRegistration {
          filter: QuoteFilter;

          /**
           * `name` might seem redundant at first, because if `filter` was a function, its name
           * could simply be used. Except that minification changes function names to something
           * else, resulting in filter name mismatches between what was specified in the HTML
           * templates and what they are at runtime.
           */
          name: string;
     }

     const registrations: QuoteFilterRegistration[] = [
          {
               filter: quoteExpiry,
               name: 'quoteExpiry',
          },
          {
               filter: isQuoteExpired,
               name: 'isQuoteExpired',
          },
          {
               filter: quoteId,
               name: 'quoteId',
          },
          {
               filter: quoteClinicPriorActsDate,
               name: 'quoteClinicPriorActsDate',
          },
          {
               filter: quoteCoverageType,
               name: 'quoteCoverageType',
          },
          {
               filter: quoteProviderNickname,
               name: 'quoteProviderNickname',
          },
          {
               filter: quoteProviderPriorActsDate,
               name: 'quoteProviderPriorActsDate',
          },
          {
               filter: quotePolicyStartDate,
               name: 'quotePolicyStartDate',
          },
          {
               filter: quotePolicyType,
               name: 'quotePolicyType',
          },
          {
               filter: quotePracticeSpecialty,
               name: 'quotePracticeSpecialty',
          },
          {
               filter: quotePracticeState,
               name: 'quotePracticeState',
          },
          {
               filter: quoteRateLiabilityLimitsIncidentDollars,
               name: 'quoteRateLiabilityLimitsIncidentDollars',
          },
          {
               filter: quoteRateLiabilityLimitsAggregateDollars,
               name: 'quoteRateLiabilityLimitsAggregateDollars',
          },
          {
               filter: quoteRateLiabilityLimits,
               name: 'quoteRateLiabilityLimits',
          },
          {
               filter: quoteRateTermDateRange,
               name: 'quoteRateTermDateRange',
          },
          {
               filter: quoteRateTermPremiumDollars,
               name: 'quoteRateTermPremiumDollars',
          },
          {
               filter: quoteRateTermYearOrdinal,
               name: 'quoteRateTermYearOrdinal',
          },
     ];
     for (const registration of registrations) {
          Vue.filter(registration.name, QuoteFilter.handleExceptions(registration.filter));
     }
};
